export type CarePlanGenieFieldType = "boolean" | "text" | "choice" | "stringArray";
export type CarePlanGenieApplyMode = "replace" | "append" | "mergeArray";

export interface CarePlanGenieFieldDefinition {
  key: string;
  label: string;
  type: CarePlanGenieFieldType;
  applyMode: CarePlanGenieApplyMode;
}

export interface CarePlanGenieOption {
  id: string;
  label: string;
  description: string;
  groupLabel?: string;
  updates: Record<string, any>;
}

export interface CarePlanGenieQuestion {
  id: string;
  title: string;
  prompt: string;
  helperText?: string;
  options: CarePlanGenieOption[];
  editableFieldKeys: string[];
}

export interface CarePlanGenieReviewItem {
  fieldKey: string;
  label: string;
  nextValue: any;
}

export interface CarePlanGeniePreview {
  valueUpdates: Record<string, any>;
  reviewItems: CarePlanGenieReviewItem[];
}

const uniqueTextLines = (value: string): string =>
  Array.from(
    new Set(
      String(value || "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean),
    ),
  ).join("\n");

export const appendCarePlanGenieSuggestion = (existingValue: string, suggestion: string): string => {
  const existing = String(existingValue || "").trim();
  const next = String(suggestion || "").trim();

  if (!existing) {
    return next;
  }

  if (!next) {
    return existing;
  }

  return uniqueTextLines(`${existing}\n${next}`);
};

export const buildCarePlanGenieStepDrafts = (
  question: CarePlanGenieQuestion,
  selectedOptionIds: string[],
): Record<string, string> => {
  const selectedOptions = question.options.filter((option) => selectedOptionIds.includes(option.id));
  const drafts: Record<string, string> = {};

  question.editableFieldKeys.forEach((fieldKey) => {
    const pieces = selectedOptions
      .map((option) => option.updates?.[fieldKey])
      .filter((value) => typeof value === "string" && String(value).trim().length > 0)
      .map((value) => String(value).trim());

    drafts[fieldKey] = Array.from(new Set(pieces)).join("\n");
  });

  return drafts;
};

const mergeArrayValues = (currentValue: any, nextValue: any) => {
  const currentItems = Array.isArray(currentValue) ? currentValue : [];
  const nextItems = Array.isArray(nextValue) ? nextValue : [];
  return Array.from(new Set([...currentItems, ...nextItems].filter(Boolean)));
};

const valuesAreEqual = (left: any, right: any) => JSON.stringify(left) === JSON.stringify(right);

export const buildCarePlanGeniePreview = (
  fieldDefinitions: Record<string, CarePlanGenieFieldDefinition>,
  questions: CarePlanGenieQuestion[],
  currentValues: Record<string, any>,
  selectedOptions: Record<string, string[]>,
  questionDrafts: Record<string, Record<string, string>>,
): CarePlanGeniePreview => {
  const aggregatedUpdates: Record<string, any> = {};

  questions.forEach((question) => {
    const selectedIds = selectedOptions[question.id] || [];
    question.options
      .filter((option) => selectedIds.includes(option.id))
      .forEach((option) => {
        Object.entries(option.updates || {}).forEach(([fieldKey, nextValue]) => {
          const fieldDefinition = fieldDefinitions[fieldKey];
          if (fieldDefinition?.applyMode === "mergeArray") {
            aggregatedUpdates[fieldKey] = mergeArrayValues(aggregatedUpdates[fieldKey], nextValue);
            return;
          }

          if (
            fieldDefinition?.applyMode === "append"
            && typeof nextValue === "string"
            && typeof aggregatedUpdates[fieldKey] === "string"
          ) {
            aggregatedUpdates[fieldKey] = appendCarePlanGenieSuggestion(aggregatedUpdates[fieldKey], nextValue);
            return;
          }

          aggregatedUpdates[fieldKey] = nextValue;
        });
      });
  });

  const draftOverrides = Object.values(questionDrafts).reduce<Record<string, string>>((accumulator, draftGroup) => {
    Object.entries(draftGroup || {}).forEach(([fieldKey, fieldValue]) => {
      accumulator[fieldKey] = fieldValue;
    });
    return accumulator;
  }, {});

  const valueUpdates = Object.keys({ ...aggregatedUpdates, ...draftOverrides }).reduce<Record<string, any>>((accumulator, fieldKey) => {
    const fieldDefinition = fieldDefinitions[fieldKey];
    if (!fieldDefinition) {
      return accumulator;
    }

    let nextValue = draftOverrides[fieldKey] !== undefined ? draftOverrides[fieldKey] : aggregatedUpdates[fieldKey];

    if (fieldDefinition.applyMode === "append" && draftOverrides[fieldKey] === undefined) {
      nextValue = appendCarePlanGenieSuggestion(String(currentValues?.[fieldKey] || ""), String(nextValue || ""));
    }

    if (fieldDefinition.applyMode === "mergeArray") {
      nextValue = mergeArrayValues(currentValues?.[fieldKey], nextValue);
    }

    if (!valuesAreEqual(currentValues?.[fieldKey], nextValue)) {
      accumulator[fieldKey] = nextValue;
    }

    return accumulator;
  }, {});

  const reviewItems = Object.entries(valueUpdates).map(([fieldKey, nextValue]) => ({
    fieldKey,
    label: fieldDefinitions[fieldKey]?.label || fieldKey,
    nextValue,
  }));

  return {
    valueUpdates,
    reviewItems,
  };
};
